import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useFlags as useLDFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { useLazyLaunchDarkly, buildLDContext } from './useLazyLaunchDarkly';

const FlagsContext = createContext(null);

// Default values while LaunchDarkly is loading
const DEFAULT_FLAGS = {
  isUserDefinedFiltersEnabled: true,
  propertyFiltersUxVariant: 'horizontal',
  isAiContextSyncEnabled: true,
  isPropertyFinderCalendlyCTAEnabled: false,
  isCountryUsingContactDetails: false,
  isLoading: true
};

/**
 * Provides real LaunchDarkly flags (only rendered when LD is initialized)
 */
function LDFlagsProvider({ children }) {
  const ldFlags = useLDFlags();
  const ldClient = useLDClient();
  const { userContext } = useLazyLaunchDarkly();
  const [isReady, setIsReady] = useState(false);

  // Wait for LD client to be ready
  useEffect(() => {
    if (!ldClient) return;
    ldClient.waitUntilReady().then(() => setIsReady(true));
  }, [ldClient]);

  // Update user context when it changes after initialization
  useEffect(() => {
    if (ldClient && userContext && isReady) {
      ldClient.identify(buildLDContext(userContext));
    }
  }, [ldClient, userContext, isReady]);

  const value = useMemo(
    () => ({
      isUserDefinedFiltersEnabled: ldFlags.isUserDefinedFiltersEnabled ?? true,
      propertyFiltersUxVariant: ldFlags.propertyFiltersUxVariant || 'horizontal',
      isAiContextSyncEnabled: ldFlags.isAiContextSyncEnabled ?? true,
      isPropertyFinderCalendlyCTAEnabled: !!ldFlags.propertyFinderCalendlyCta,
      isCountryUsingContactDetails: ldFlags.isCountryUsingContactDetails === 'none' ? false : ldFlags.isCountryUsingContactDetails,
      isLoading: !isReady
    }),
    [ldFlags, isReady]
  );

  return <FlagsContext.Provider value={value}>{children}</FlagsContext.Provider>;
}

/**
 * FlagsContextProvider - Provides flag values.
 * Returns defaults with isLoading=true until LD is initialized.
 */
export function FlagsContextProvider({ children }) {
  const { isInitialized } = useLazyLaunchDarkly();

  if (isInitialized) {
    return <LDFlagsProvider>{children}</LDFlagsProvider>;
  }

  return <FlagsContext.Provider value={DEFAULT_FLAGS}>{children}</FlagsContext.Provider>;
}

/**
 * useFlags - Returns current flag values.
 * Does NOT trigger LD initialization. Use useFlagsWithInit() when you need real flag values.
 */
export function useFlags() {
  return useContext(FlagsContext) ?? DEFAULT_FLAGS;
}

/**
 * useFlagsWithInit - Returns flag values and triggers LD initialization.
 * Use this on pages that depend on feature flag values.
 */
export function useFlagsWithInit() {
  const { isInitialized, isInitializing, initializeLaunchDarkly } = useLazyLaunchDarkly();
  const flags = useFlags();

  useEffect(() => {
    if (!isInitialized && !isInitializing) {
      initializeLaunchDarkly();
    }
  }, [isInitialized, isInitializing, initializeLaunchDarkly]);

  return useMemo(
    () => ({
      ...flags,
      isLoading: !isInitialized || isInitializing || flags.isLoading
    }),
    [flags, isInitialized, isInitializing]
  );
}
